// First Flight — the guided first sortie's screen half.
//
// The server owns the syllabus (internal/game/firstflight.go): it watches the
// stick, spawns the greenhorns, forgives the ground, and narrates each beat as
// one `ff_*` event. This module is the part the player actually sees — a
// bottom-centre banner card with the current instruction, a step rail, and a
// SKIP TO BATTLE escape that is never more than one tap away — plus the two
// bits of tidying a first sortie needs: quiet the gauges that have nothing to
// say yet, and stamp FIRST BLOOD on the moment the whole thing is built around.
//
// Deliberately dumb: it holds no timers and no completion logic of its own, so
// it can never disagree with the server about which beat you're on.

// One entry per beat the server can announce. Desktop and touch get different
// verbs for the same action, because "move your mouse" is a lie on a phone.
const BEATS = {
  ff_start: {
    step: 0,
    text: "MOVE YOUR MOUSE TO FLY",
    touch: "SLIDE YOUR LEFT THUMB TO FLY",
    sub: "she follows the crosshair — try a turn",
  },
  ff_first_input: {
    step: 1,
    text: "BANDIT DEAD AHEAD — PUT THE GOLD ◆ UNDER YOUR GUNS",
    touch: "BANDIT DEAD AHEAD — LINE UP THE GOLD ◆ AND TAP FIRE",
    sub: "the gold diamond is where he'll be, not where he is",
  },
  ff_first_kill: {
    step: 2,
    text: "AGAIN — THIS ONE'S RUNNING",
    touch: "AGAIN — THIS ONE'S RUNNING",
    sub: "lead the turn: aim at the diamond, not the plane",
  },
  ff_moving_kill: {
    step: 3,
    text: "YOU'RE READY — GOOD HUNTING",
    touch: "YOU'RE READY — GOOD HUNTING",
    sub: "",
  },
  ff_handoff: {
    step: 3,
    text: "DROPPING YOU INTO THE BATTLE",
    touch: "DROPPING YOU INTO THE BATTLE",
    sub: "",
  },
};

// How long the "you're ready" beat holds before the handoff request goes out.
// Long enough to read, short enough that nobody wonders if it's stuck.
const HANDOFF_HOLD_SEC = 2.2;

// Gauges the first ninety seconds have nothing to say about. TRIM is a control
// nobody has been taught yet, BMB is ordnance the trainer will never drop, and
// the hint bar is a wall of keybinds competing with the one instruction that
// matters right now.
const QUIETED = ["trim-gauge", "bombs-gauge", "hint-bar"];

export class FirstFlight {
  /**
   * @param {object} opts
   * @param {boolean} opts.touch  touch layout (different verbs, bigger SKIP)
   * @param {() => void} opts.onGraduate  ask the server for the shared world
   * @param {(name: string, params?: object) => void} [opts.analytics]
   */
  constructor({ touch = false, onGraduate = () => {}, analytics = () => {} } = {}) {
    this.touch = touch;
    this.onGraduate = onGraduate;
    this.analytics = analytics;
    this.active = false;
    this.step = -1;
    this.startedAt = 0;
    this.mercies = 0;
    this.el = null;
    this._handoffAt = 0;
    this._asked = false;
    this._warnTimer = null;
  }

  /** Raise the overlay for a sortie that just started. */
  begin(now) {
    if (this.active) return;
    this.active = true;
    this.startedAt = now;
    this._asked = false;
    this._handoffAt = 0;
    this._build();
    this._quiet(true);
  }

  /**
   * Take down the overlay and hand the gauges back. Called at the handoff and
   * on skip — after this the client is an ordinary client again.
   */
  end() {
    if (!this.active) return;
    this.active = false;
    this._quiet(false);
    clearTimeout(this._warnTimer);
    if (this.el) {
      this.el.classList.remove("show");
      const el = this.el;
      setTimeout(() => el.remove(), 420);
      this.el = null;
    }
    try {
      localStorage.setItem("va-first-sortie", "1");
    } catch {
      /* storage-blocked: the server's empty-career check is the real guard */
    }
  }

  /**
   * Feed one server event. Returns true if it was a First Flight beat (so the
   * caller knows not to also treat it as ordinary combat chatter).
   */
  onEvent(ev, now) {
    if (!ev.e || ev.e.slice(0, 3) !== "ff_") return false;
    if (ev.e === "ff_mercy") {
      this.mercies++;
      this._warn();
      return true;
    }
    if (ev.e === "ff_skip") return true;
    const beat = BEATS[ev.e];
    if (!beat) return true;
    if (!this.active) this.begin(now);
    this.step = beat.step;
    this._render(beat);
    if (ev.e === "ff_first_kill") this.firstBlood();
    if (ev.e === "ff_handoff") this._handoffAt = now;
    return true;
  }

  /** Per-frame tick: the only thing it drives is the handoff request. */
  update(now) {
    if (!this.active || this._asked || !this._handoffAt) return;
    if (now - this._handoffAt < HANDOFF_HOLD_SEC) return;
    this._asked = true;
    this.analytics("ff_complete", { duration_sec: Math.round(now - this.startedAt) });
    this.onGraduate();
  }

  /** The player pressed SKIP TO BATTLE. */
  skip() {
    if (!this.active || this._asked) return;
    this._asked = true;
    this.analytics("ff_skip", { at_step: this.step });
    this.onGraduate();
  }

  /**
   * The moment the whole sortie exists for. A stamp, not a cutscene — it
   * shares the kill-confirm's rhythm so the first one a player ever sees reads
   * the same as every one after it.
   */
  firstBlood() {
    const el = document.createElement("div");
    el.id = "ff-firstblood";
    el.innerHTML = '<b>FIRST BLOOD</b><span>your first kill is on the books</span>';
    document.getElementById("ui")?.appendChild(el);
    requestAnimationFrame(() => el.classList.add("show"));
    setTimeout(() => {
      el.classList.remove("show");
      setTimeout(() => el.remove(), 500);
    }, 2600);
  }

  // ---- internals ----

  _build() {
    if (this.el) return;
    const el = document.createElement("div");
    el.id = "ff-layer";
    el.innerHTML =
      '<div class="ff-card">' +
      '<div class="ff-rail"><i></i><i></i><i></i><i></i></div>' +
      '<div class="ff-text"></div>' +
      '<div class="ff-sub"></div>' +
      '<button class="ff-skip" type="button">SKIP TO BATTLE ✕</button>' +
      "</div>";
    el.querySelector(".ff-skip").addEventListener("click", () => this.skip());
    document.getElementById("ui")?.appendChild(el);
    this.el = el;
    requestAnimationFrame(() => el.classList.add("show"));
  }

  _render(beat) {
    if (!this.el) this._build();
    if (!this.el) return;
    this.el.querySelector(".ff-text").textContent = this.touch ? beat.touch : beat.text;
    const sub = this.el.querySelector(".ff-sub");
    sub.textContent = beat.sub;
    sub.classList.toggle("hidden", !beat.sub);
    const dots = this.el.querySelectorAll(".ff-rail i");
    dots.forEach((d, i) => d.classList.toggle("on", i <= beat.step));
    // The last beat has no skipping left to do.
    this.el.querySelector(".ff-skip").classList.toggle("hidden", beat.step >= 3);
  }

  // A forgiven crash. The warning tells you the recovery, not just the problem
  // — the house rule for every warning in the game.
  _warn() {
    if (!this.el) return;
    const sub = this.el.querySelector(".ff-sub");
    sub.textContent = "⚠ THAT WAS THE GROUND — PULL BACK TO CLIMB. WE PUT YOU BACK UP ⚠";
    sub.classList.remove("hidden");
    sub.classList.add("ff-warn");
    clearTimeout(this._warnTimer);
    this._warnTimer = setTimeout(() => sub.classList.remove("ff-warn"), 4000);
  }

  _quiet(on) {
    for (const id of QUIETED) {
      document.getElementById(id)?.classList.toggle("ff-quiet", on);
    }
  }
}

/**
 * routeToFirstFlight decides whether this TAKE OFF should ask for a guided
 * sortie. The server has the authoritative say (an empty career on the join
 * token — see FirstFlightEligible); this only avoids asking when we already
 * know the answer is no.
 *
 * The trap worth naming: an EPHEMERAL visitor — a private window, a
 * third-party-partitioned embed on a portal — never persists anything, so
 * "no va-first-sortie key" is true on every single load. Routing them
 * automatically would loop them through the tutorial forever, so they always
 * take today's path.
 */
export function routeToFirstFlight(opts) {
  return firstFlightRouteReason(opts) === "routed";
}

/**
 * The same decision, showing its work — which is what the funnel needs.
 *
 * A tutorial's conversion rate is meaningless without its denominator, and the
 * denominator here is not "everyone who arrived": most people who press TAKE
 * OFF are returning pilots who should never see a tutorial, and routing them
 * would be the bug. So the number worth watching is how many of the visitors
 * we *judged* to be brand new went through the door, and every visitor we
 * judged otherwise carries the reason why.
 *
 * That matters in both directions. Too many `returning` on a day of heavy new
 * traffic means the newness test is reading cleared storage as a veteran and
 * dropping first-timers into the deep end. A wall of `ephemeral` means the
 * portal embeds are where the new players are, and they are the one group this
 * routing deliberately cannot help — which is worth knowing before anyone
 * concludes the tutorial "isn't converting".
 *
 * @returns {"routed"|"returning"|"ephemeral"|"no_storage"|"theater"|"training"|"replay"|"already_flown"}
 */
export function firstFlightRouteReason({ isNew, ephemeral, theater, trainingServer, replay }) {
  if (replay) return "replay";
  if (trainingServer) return "training";
  if (ephemeral) return "ephemeral";
  if ((theater || "air") !== "air") return "theater"; // the syllabus teaches air combat
  try {
    if (localStorage.getItem("va-first-sortie")) return "already_flown";
  } catch {
    return "no_storage"; // storage blocked: same trap as ephemeral
  }
  return isNew ? "routed" : "returning";
}
